import Gtm from "@/infrastructure/observability/google-tag-manager.service";
import type { ISnowPlowAbTestAction } from "@/types/analytics-types/analytics.types";
import {
  snowPlowAbTestActionToRequest,
  SnowplowActionTypes,
} from "@/types/analytics-types/analytics.types";
import {
  mapSliderItemToSnowplowContext,
  mapSliderToSnowplowData,
  type SliderRecommendations,
} from "@/types/slider.types";

import {
  trackPageView,
  trackSelfDescribingEvent,
  trackStructEvent,
  addGlobalContexts,
  clearGlobalContexts,
} from "@snowplow/browser-tracker";

import {
  HomepageVNextRolloutTest,
  SnowplowSchemas,
  WorksPageVNextRolloutTest,
} from "./constants";
import { isBoolean, isNumber, isString } from "./type-guards.utility";

/** Called when Snowplow is ready to receive tracking events */
let resolveSnowplowReady: () => void;

/** Promise that resolves when Snowplow is ready to track events */
const snowplowReady = new Promise<void>((resolve) => {
  resolveSnowplowReady = resolve;
});

declare global {
  interface Window {
    isIE: boolean;
    isSafari: boolean;
  }
}

export const initSnowplow = async () => {
  if (
    typeof window !== "undefined" &&
    process.env.NEXT_PUBLIC_SNOWPLOW_TRACKER_ID &&
    process.env.NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL
  ) {
    const { newTracker } = await import("@snowplow/browser-tracker");

    const { PerformanceTimingPlugin } =
      await import("@snowplow/browser-plugin-performance-timing");

    const { LinkClickTrackingPlugin, enableLinkClickTracking } =
      await import("@snowplow/browser-plugin-link-click-tracking");

    newTracker(
      process.env.NEXT_PUBLIC_SNOWPLOW_TRACKER_ID,
      process.env.NEXT_PUBLIC_SNOWPLOW_COLLECTOR_URL,
      {
        appId: "js-website",
        plugins: [PerformanceTimingPlugin(), LinkClickTrackingPlugin()],
        platform: "web",
        eventMethod: "post",
        discoverRootDomain: true,
        stateStorageStrategy: "cookieAndLocalStorage",
        contexts: {
          webPage: true,
        },
        bufferSize: 1,
      },
    );
    enableLinkClickTracking();
    resolveSnowplowReady();
  }
};

export const updateUserAnalyticsContext = (
  tident: string,
  sessionId: string,
  idAccount: number | undefined,
  mobileAppVersion?: string,
): void => {
  const contextEntity = {
    schema: "iglu:com.thriftbooks/user/jsonschema/2-0-0", // cSpell:ignore iglu tident
    data: { idAccount, TIdent: tident, sessionId, mobileAppVersion },
  };

  clearGlobalContexts();
  addGlobalContexts([contextEntity]);
};

export const virtualPageviewAnalyticsHandler = async (
  tident: string,
  sessionId: string,
  idAccount: number | undefined,
  isInit: boolean,
  mobileAppVersion?: string,
) => {
  if (isInit) {
    await initSnowplow();
  } else {
    Gtm.sendVirtualPageviewEvent();
  }
  updateUserAnalyticsContext(tident, sessionId, idAccount, mobileAppVersion);
  trackPageView();
};

export const trackStructuredSnowplowEvent = async (
  category: string,
  action: string,
  label?: string,
  property?: string,
  value?: number,
) => {
  trackStructEvent({
    category,
    action,
    label,
    property,
    value,
  });
};

/**
 * Checks if the provided value matches the expected Snowplow type.
 * @param value - The value to validate (string, number, or boolean).
 * @param expectedType - The expected Snowplow type ("string", "double", "int", "bool").
 * @returns True if the value matches the expected type; otherwise, false.
 */
const isDataOfExpectedType = (
  value: string | number | boolean,
  expectedType: string,
): boolean => {
  if (
    !expectedType ||
    value === null || // value can be a boolean so we can't just check for truthiness
    value === undefined
  ) {
    return false;
  }

  switch (expectedType) {
    case SnowplowActionTypes.VALUE_STRING:
      return isString(value);
    case SnowplowActionTypes.VALUE_INT:
    case SnowplowActionTypes.VALUE_DOUBLE:
      return isNumber(value);
    case SnowplowActionTypes.VALUE_BOOL:
      return isBoolean(value);
    default:
      return false;
  }
};

/**
 * Sets the appropriate value field in the event data object based on the provided type and value.
 * @param data - The event data object to update.
 * @param valueType - The type of the value ("string", "double", "int", "bool").
 * @param contextValue - The value to set (string, number, or boolean).
 * @returns The updated event data object with the correct value field set.
 */
const setEventDataContextValue = (
  data: ISnowPlowAbTestAction,
  valueType: string,
  contextValue: string | number | boolean,
): ISnowPlowAbTestAction => {
  const dataCopy = { ...data };

  if (isString(contextValue)) {
    dataCopy.valueType = SnowplowActionTypes.VALUE_STRING;
    dataCopy.valueString = contextValue;
    return dataCopy;
  }

  if (isBoolean(contextValue)) {
    dataCopy.valueType = SnowplowActionTypes.VALUE_BOOL;
    dataCopy.valueBool = contextValue;
    return dataCopy;
  }

  if (isNumber(contextValue)) {
    if (valueType === SnowplowActionTypes.VALUE_INT) {
      dataCopy.valueType = SnowplowActionTypes.VALUE_INT;
      dataCopy.valueInt = contextValue;
    } else if (valueType === SnowplowActionTypes.VALUE_DOUBLE) {
      dataCopy.valueType = SnowplowActionTypes.VALUE_DOUBLE;
      dataCopy.valueDouble = contextValue;
    }
    return dataCopy;
  }

  return dataCopy;
};

/**
 * Fires off an action related to A/B tests using Snowplow.
 * @param abTestName - The name of the A/B test associated with this event.
 * @param actionName - The specific user action being tracked (e.g., "click").
 * @param abTestEventId - An identifier for the test action (range: 0–19), used for reporting.
 * @param valueType - Optional. The type of the context value ("string", "double", "int", "bool").
 * @param contextValue - Optional. Additional value to include with the event.
 */
export const trackAction = (
  abTestName: string,
  actionName: string,
  abTestEventId: number,
  valueType: SnowplowActionTypes | null = null,
  contextValue: string | number | boolean | null = null,
): void => {
  if (
    !abTestName ||
    !actionName ||
    abTestEventId === null ||
    abTestEventId === undefined
  ) {
    return;
  }

  let data: ISnowPlowAbTestAction = {
    abTestName,
    actionName,
    abTestEventId,
  };

  // contextValue could be a boolean false, which is a valid value, so we can't just check for truthiness
  if (valueType && contextValue !== null && contextValue !== undefined) {
    if (isDataOfExpectedType(contextValue, valueType)) {
      data = setEventDataContextValue(data, valueType, contextValue);
    }
  }

  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchemas.AB_TEST_ACTION,
      data: snowPlowAbTestActionToRequest(data),
    },
  });
};

/** Tracks a rendered homepage for the CDN-assigned homepage rollout test. */
export const trackHomepageViewed = (): void => {
  trackAction(
    HomepageVNextRolloutTest.TestName,
    HomepageVNextRolloutTest.HomepageViewedActionName,
    HomepageVNextRolloutTest.HomepageViewedEventId,
  );
};

/** Tracks a rendered Works page for the CDN-assigned Works rollout test. */
export const trackWorksPageViewed = (): void => {
  trackAction(
    WorksPageVNextRolloutTest.TestName,
    WorksPageVNextRolloutTest.WorksPageViewedActionName,
    WorksPageVNextRolloutTest.WorksPageViewedEventId,
  );
};

/**
 * Tracks a list of recommendation sliders shown to the user.
 * @param slider The slider data
 * @param sliderPosition The index of this slider relative to others
 */
export const trackRecommendationsShown = async (
  slider: SliderRecommendations,
  sliderPosition: number = 0,
): Promise<void> => {
  if (!slider || !slider.idRecommendationCorrelation) {
    return;
  }

  const contexts = slider.items.map((item, workIndex) => ({
    schema: SnowplowSchemas.RECOMMENDED_WORK,
    data: mapSliderItemToSnowplowContext(item, workIndex),
  }));

  await snowplowReady;

  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchemas.RECOMMENDATION_SHOWN,
      data: mapSliderToSnowplowData(slider, sliderPosition),
    },
    context: contexts,
  });
};

/**
 * Tracks when a user adds an item to the cart
 * @param interactionType the type of interaction
 * @param idiq the idiq being added
 * @param quantity optional, the qty added
 * @param removed_reason optional, the reason why an item was removed from the cart
 */
export const trackAddToCart = (
  interactionType: "added" | "removed" | "modified" | "refreshed",
  idiq: number,
  quantity?: number,
  removed_reason?:
    | "Customer_Deleted"
    | "Customer_Moved_To_Saved_For_Later"
    | "Customer_Moved_To_list"
    | "Out_Of_Stock"
    | "Purchased",
): void => {
  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchemas.CART_ITEM_INTERACTION,
      data: {
        interaction_type: interactionType,
        quantity,
        idiq,
        removed_reason,
      },
    },
  });
};

export type ListItemInteractionType = "added" | "removed";

/**
 * Tracks an item being added/removed to a wishlist
 * @param interactionType the type of interaction (added/removed)
 * @param listName the list's name
 * @param idList the list id
 * @param idAmazon the edition
 * @param idWork the work
 */
export const trackListItemInteraction = (
  interactionType: ListItemInteractionType,
  listName: string,
  idList: number,
  idAmazon: number,
  idWork: number,
): void => {
  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchemas.LIST_ITEM_INTERACTION,
      data: {
        interaction_type: interactionType,
        list_name: listName,
        list_id: idList,
        id_amazon: idAmazon,
        id_work: idWork,
      },
    },
  });
};

/**
 * Tracks an item being added to a wishlist.
 * @param listName the list's name
 * @param idList the list id
 * @param idAmazon the edition
 * @param idWork the work
 */
export const trackAddedToList = (
  listName: string,
  idList: number,
  idAmazon: number,
  idWork: number,
): void => {
  trackListItemInteraction("added", listName, idList, idAmazon, idWork);
};

/**
 * Tracks an item being removed from a wishlist.
 * @param listName the list's name
 * @param idList the list id
 * @param idAmazon the edition
 * @param idWork the work
 */
export const trackRemovedFromList = (
  listName: string,
  idList: number,
  idAmazon: number,
  idWork: number,
): void => {
  trackListItemInteraction("removed", listName, idList, idAmazon, idWork);
};
